import { Fragment } from 'react';
import type { WorkflowFieldDef } from '../../lib/workflows/config';
import { SPEC_LOOKUPS } from '../../lib/workflows/specLookups';
import { fieldWarning, formatInvalid, formatMessage, normalizeHeight, sanitizeFieldInput } from '../../lib/workflows/fieldFormat';

/**
 * Options for a spec-driven lookup field (place of birth / citizenship).
 *
 * The spec files list codes in their own order (roughly grouped, not alphabetical), which makes a
 * long select hard to scan, so both lists are sorted by visible label. Sorting is by label rather
 * than code because the label is what the operator reads.
 */
export function lookupOptions(spec: string, kind: 'pob' | 'ctz'): { value: string; label: string }[] {
  const opts = (SPEC_LOOKUPS[spec]?.[kind] ?? [])
    .map((c) => ({ value: c.value, label: c.description }))
    // localeCompare so accented and punctuated names sort where a reader expects, not by code point.
    .sort((a, b) => a.label.localeCompare(b.label));
  // Then pin United States to the top of the citizenship list — it's the overwhelmingly common answer,
  // and this must happen AFTER the sort or it would be shuffled back into the Us.
  if (kind === 'ctz') {
    const i = opts.findIndex((o) => o.value === 'US');
    if (i > 0) opts.unshift(opts.splice(i, 1)[0]!);
  }
  return opts;
}

/** True when a field is required-but-empty, or has a value that fails its type or its format. */
export function isFieldInvalid(f: WorkflowFieldDef, value: string | undefined): boolean {
  const v = (value ?? '').trim();
  if (f.required && !v) return true;
  if (!v) return false; // optional + empty is fine
  if (f.type === 'number' && Number.isNaN(Number(v))) return true;
  if (f.type === 'date' && Number.isNaN(Date.parse(v))) return true;
  // Fields whose shape the EBTS fixes (height) — a wrong shape here is an EFT violation, not a typo the
  // FBI will overlook, so it is caught in the form rather than on rejection days later.
  return formatInvalid(f.name, v);
}

/** A single demographic form input (text/number/date/select) — shared by capture, edit, and correct. */
export function FieldInput({ f, value, options, onChange, invalid }: {
  f: WorkflowFieldDef;
  value: string;
  options?: { value: string; label: string }[] | undefined;
  onChange: (v: string) => void;
  /** Show the error state (red border + message) — driven by the parent once the user has tried to proceed. */
  invalid?: boolean;
}) {
  const opts = options ?? f.options;
  const missing = !((value ?? '').trim());
  return (
    <label
      className={`field${f.type === 'textarea' && !f.colSpan ? ' field-wide' : ''}${invalid ? ' has-error' : ''}`}
      /*
       * Spans come from the configuration. Clamped to the grid: a 3-wide field has one column to live in on
       * a phone, and `span 4` on a three-column grid silently overflows the container rather than wrapping.
       */
      style={{
        ...(f.colSpan && f.colSpan > 1 ? { gridColumn: `span ${Math.min(3, Math.floor(f.colSpan))}` } : {}),
        ...(f.rowSpan && f.rowSpan > 1 ? { gridRow: `span ${Math.floor(f.rowSpan)}` } : {}),
      }}
    >
      <span className="field-label">{f.label}{f.required && <em className="req">*</em>}</span>
      {f.type === 'textarea' ? (
        // Newlines are meaningful here: buildCustomEft rewrites them to CRLF for the EBTS fields listed
        // in postRules.crlfFields, so the control must be able to hold them at all.
        <textarea className={`input field-multiline${invalid ? ' invalid' : ''}`} rows={3}
          value={value} placeholder={f.placeholder ?? ''} onChange={(e) => onChange(e.target.value)}
          aria-invalid={invalid || undefined} />
      ) : f.type === 'select' ? (
        <select className={`select${invalid ? ' invalid' : ''}`} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select…</option>
          {opts?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      ) : (
        <input
          className={`input${invalid ? ' invalid' : ''}`}
          type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
          value={value}
          placeholder={f.placeholder ?? ''}
          // Constrained as it is typed (height takes three digits and nothing else), then normalized on
          // blur so 5'9" becomes 509 rather than being rejected for a format the operator can't guess.
          onChange={(e) => onChange(sanitizeFieldInput(f.name, e.target.value))}
          onBlur={(e) => { if (f.name === 'height') onChange(normalizeHeight(e.target.value)); }}
          {...(f.name === 'height' ? { inputMode: 'numeric' as const, maxLength: 8 } : {})}
          aria-invalid={invalid || undefined} />
      )}
      {invalid && (
        <span className="field-error">
          {missing ? 'This field is required.' : formatMessage(f.name) ?? 'Please enter a valid value.'}
        </span>
      )}
      {f.help && <span className="field-help">{f.help}</span>}
      {/* Standing caution for a field that is safe blank and harmful to guess at — shown always, not only on
          error, because by the time it is an error the submission has already been rejected. */}
      {fieldWarning(f.name) && (
        <span className="field-warn">
          <strong>Leave blank unless instructed.</strong> {fieldWarning(f.name)}
        </span>
      )}
    </label>
  );
}

/**
 * Render a workflow's demographic fields into a `.demo-grid` from a value map, calling onChange(name, value).
 * `specification` drives the pob/ctz spec lookups.
 */
export function DemographicsGrid({ fields, specification, values, onChange, showErrors }: {
  fields: WorkflowFieldDef[];
  specification: string;
  values: Record<string, string>;
  onChange: (name: string, value: string) => void;
  /** When set, required-missing / invalid fields render in red. */
  showErrors?: boolean;
}) {
  return (
    <div className="demo-grid">
      {fields.map((f) => (
        <Fragment key={f.name}>
          {/* A configured row break: an empty full-width cell, so what follows starts a fresh row. */}
          {f.breakBefore && <div className="field-break" aria-hidden="true" />}
          <FieldInput
            f={f}
            value={values[f.name] ?? ''}
            options={f.lookup ? lookupOptions(specification, f.lookup) : f.options}
            onChange={(v) => onChange(f.name, v)}
            invalid={Boolean(showErrors) && isFieldInvalid(f, values[f.name])}
          />
        </Fragment>
      ))}
    </div>
  );
}
